// pages/us/insights/[url_slug].jsx
import { Global, Collection } from '@sourceflow-uk/sourceflow-sdk';
import SeoHead from '../../../components/SeoHead';
import { Helmet } from 'react-helmet';
import LatestNews from '../../../components/LatestNews';
import { Container } from 'reactstrap';
import NewBlogDetails from '../../../components/NewBlogDetails';
import NewSubBanner from '../../../components/NewSubBanner';
import NewColumnWithForm from '../../../components/NewColumnWithForm';
import NewBlogDetails2 from '../../../components/NewBlogDetails2';
import MainBanner from "../../../components/MainBanner";
import AllBlogs from "../../../components/AllBlogs";
import NewFeaturedBlogs from "../../../components/NewFeaturedBlogs";
import { DynamicPageSeo } from "@/ui/DynamicPageSeo";
import { ContentBlocks } from "@/ui/ContentBlocks";
import useDynamicPages from "@/hooks/useDynamicPages";

function USPostView({ global, page, all_news, related_news }) {
  const breadcrumbList = [
    {
      "@type": "ListItem",
      "name": "Lorien",
      "position": 1,
      "item": { "@type": "Thing", "@id": `https://www.lorienglobal.com/us` }
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Insights",
      "item": { "@type": "Thing", "@id": `https://www.lorienglobal.com/us/insights` }
    }
  ];

  return (
    <>
      <SeoHead
        page_name={`${page.title} | Lorien${page.tag.includes("News & Insights") ? " Insights" : "" }`}
        description={page.tag.includes("Case Study") ? `Discover insights from "${page.title}" and how Lorien delivers effective solutions across Technology in the US.` : `Expert insights on ${page.title} from Lorien, specialists in Technology in the US.`}
        canonical_link={`insights/${page.url_slug}`}
        og_image={page.image}
        og_img_alt={`${page.title}`}
        en_gb={false}
        en_us={true}
        removeUKHrefLang={page.country?.includes("United Kingdom") || page.country?.includes("All") ? false : true}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbList
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "description": page?.short_description || "",
            "isFamilyFriendly": "http://schema.org/True",
            "image": page?.image || "",
            "dateModified": page?.updated_at || "",
            "datePublished": page?.created_at || "",
            "name": page?.title || "",
            "headline": page?.title || "",
            "articleBody": page?.content ? page.content.replace(/<[^>]*>/g, "") : "",
            "publisher": {
              "@type": "Organization",
              "name": "Lorien",
              "sameAs": "https://www.lorienglobal.com/us",
              "logo": {
                "@type": "ImageObject",
                "url": "https://cdn.sourceflow.co.uk/wijxmc47ccusm0jll37684eh7usc"
              }
            },
            "author": {
              "@type": "Person",
              "image": page?.author_image || "",
              "name": page?.author || ""
            }
          })}
        </script>
      </Helmet>

      <NewSubBanner
        global={global}
        pathPrefix={`us.${page.url_slug}.insights_page.main_banner`}
        customClass={``}
        content={<h1>{page.title}</h1>}
        imageURL1={`/site-assets/images/main-banner.jpg`}
        imageALT1={`Main Banner`}
        imageURL2={`${page.image ?? "/site-assets/images/sub-banner-insights.png"}`}
        imageALT2={`Overlay Main Banner`}
        displayImage1={false}
        editableContent={false}
        buttonLink={`${
          page.tag.includes("Case Study")
            ? "/us/insights/case-study"
            : page.tag.includes("Guides & Reports")
            ? "/us/insights/guides-and-reports"
            : "/us/insights"
        }`}
        buttonTitle={`${
          page.tag.includes("Case Study")
            ? "Back to Case Study"
            : page.tag.includes("Guides & Reports")
            ? "Back to Guides & Reports"
            : "Back to insights"
        }`}
        post={page}
        buttonLink2={`${
          page.tag.includes("Guides & Reports") && page?.form_id ? "#download-form" : ""
        }`}
        buttonTitle2={`${
          page.tag.includes("Guides & Reports") && page?.form_id ? "Download Report" : ""
        }`}
        breadcrumb_title={`${page?.title}`}
        secondary_breadcrumb_url={"/us/insights"}
        secondary_breadcrumb_title={"Insights"}
      />

      {page.tag.includes("Guides & Reports") ? (
        <NewBlogDetails
          global={global}
          pathPrefix={`us.insights_page.guides_and_reports.new_blog_details`}
          page={page}
          related_news={related_news}
          page_prefix={`insights`}
          share_heading={`Share Report`}
          className={`py-5 guides-and-reports-page`}
          formId={page?.form_id}
          locale={`us`}
        />
      ) : page.tag.includes("Case Study") ? (
        page.case_study_tabs?.length >= 1 && (
          <NewBlogDetails2
            global={global}
            pathPrefix={`insights_page.new_blog_details_2`}
            page={page}
          />
        )
      ) : (
        <NewBlogDetails
          global={global}
          pathPrefix={`insights_page.new_blog_details`}
          page={page}
          related_news={related_news}
          page_prefix={`insights`}
          locale={`us`}
        />
      )}

      {(page.tag.includes("Guides & Reports") || page.tag.includes("Case Study")) ? (
        <>
          <Container><hr /></Container>
          <LatestNews
            global={global}
            pathPrefix={`us.${page.url_slug}.insights_page.guides_and_reports.latest_news`}
            customClass={`py-5 white-card`}
            post={all_news}
            content={<h2>{page.tag.includes("Guides & Reports") ? "Other Guides and Reports" : "Other Case Studies"}</h2>}
            buttonLink={``}
            buttonTitle={``}
            content_align={`left`}
            footer_buttonLink={`/us/insights${page.tag.includes("Guides & Reports") ? "/guides-and-reports" : "/case-study"}`}
            footer_buttonLink_title={`View All`}
            locale={`us/`}
          />
        </>
      ) : null}

      <NewColumnWithForm
        global={global}
        pathPrefix={`us.${page.url_slug}.insights_page.new_column_with_form`}
        customClass={`pb-5`}
        heading={`How can we help you?`}
        formId={`c5e3d9d7-fe11-4ba2-a819-f35994bb78e9`}
        locale={`us`}
      />
    </>
  );
}

function USTagView({ global, page, all_news }) {
  return (
    <>
      <SeoHead
        page_name={`${page.title} | The global house of tech and transformation recruitment | Lorien | United States`}
        canonical_link={`insights/${page.url_slug}`}
        en_gb={false}
        en_us={true}
      />

      <MainBanner
        global={global}
        pathPrefix={`us.insights_page.main_banner`}
        customClass={``}
        content={<h1>{page.title}</h1>}
        imageURL1={`/site-assets/images/main-banner.jpg`}
        imageALT1={`Main Banner`}
        imageURL2={`/site-assets/images/sub-banner-insights.png`}
        imageALT2={`Overlay Main Banner`}
        editableContent={false}
        displayImage1={false}
      />

      <NewFeaturedBlogs
        global={global}
        pathPrefix={`us.${page.url_slug}.insights_page.new_featured_blogs`}
        customClass={`py-5 grayGradientBg`}
        content={<h2>Featured {page.title}</h2>}
        post={all_news}
        locale={`us`}
        socialPagePrefix={`us/insights/${page.url_slug}`}
      />

      <AllBlogs
        global={global}
        pathPrefix={`us.news_page.all_blogs`}
        customClass={`py-5 lightGreybg`}
        post={all_news}
        ParentNews={false}
        tagName={`${page.title}`}
        locale={`us/`}
        heading={`All ${page.title}`}
      />

      <NewColumnWithForm
        global={global}
        pathPrefix={`us.${page.url_slug}.insights_page.new_column_with_form`}
        customClass={`pb-5`}
        heading={`How can we help you?`}
        formId={`c5e3d9d7-fe11-4ba2-a819-f35994bb78e9`}
        locale={`us`}
      />
    </>
  );
}

export default function USInsightsUnified(props) {
  // Preserve builder rendering if used elsewhere
  if (props.isBuilder) {
    const { seo, title, canonical, data, prefix } = props;
    return (
      <>
        <DynamicPageSeo
          title={seo?.title || title}
          description={seo?.description || ""}
          canonical={seo?.canonical || canonical}
          keywords={seo?.keywords || ""}
          robots={seo?.robots || ""}
          json_schema={seo?.json_schema || ""}
          image={seo?.open_graph?.image?.url || ""}
        />
        <ContentBlocks data={data} prefix={prefix} />
      </>
    );
  }
  return props.mode === "post" ? <USPostView {...props} /> : <USTagView {...props} />;
}

export async function getStaticProps({ params }) {
  const global = (await Global()).toJson();

  // Try POST first
  let postPage = null;
  try {
    postPage = (await Collection('news')).getJsonItem(params.url_slug, 'url_slug');
  } catch (_) {}

  if (postPage && postPage.url_slug) {
    const news = (await Collection('news'))
      .filter((post) => post.country?.en == "United States" || post.country?.en == "All")
      .filter(
        (post) =>
          Array.isArray(post.tag?.en) &&
          post.tag.en.some((tag) => postPage.tag?.includes(tag))
      );

    const all_news = news.orderBy('date').getItems().slice(0, 8);
    const related_news = news.orderBy('order').getItems().slice(0, 2);

    return {
      props: {
        global,
        mode: "post",
        page: postPage,
        all_news,
        related_news
      }
    };
  }

  // Fallback to TAG
  let tagPage = null;
  try {
    tagPage = (await Collection('news_tag')).getJsonItem(params.url_slug, 'url_slug');
  } catch (_) {}

  if (!tagPage) {
    return { notFound: true };
  }

  const tagName = tagPage.title;
  const newsForTag = (await Collection('news'))
    .filter((post) => post.country?.en == "United States" || post.country?.en == "All")
    .filter((post) => post.tag?.en?.includes(tagName));

  const all_news = newsForTag
    .orderBy((n) => Date.parse(n.date?.en), 'desc')
    .getItems();

  return {
    props: {
      global,
      mode: "tag",
      page: tagPage,
      all_news
    }
  };
}

export async function getStaticPaths() {
  // Keep your dynamic builder exclusion logic
  const pages = useDynamicPages()
    .filter((page) => page.url_slug.includes("us/insights/"))
    .filter((i) => !i.is_draft)
    .map((i) => i.url_slug.split("/").pop());

  const postPaths = (await Collection('news'))
    .filter((post) =>
      !pages.includes(post.url_slug) &&
      (post.country?.en == "United States" || post.country?.en == "All")
    )
    .getPaths('url_slug');

  const tagPaths = (await Collection('news_tag')).getPaths('url_slug');

  return {
    paths: [...postPaths, ...tagPaths],
    fallback: false
  };
}
